import React, { useRef } from 'react';
import { Provider } from 'react-redux';
import createSagaMiddleware from 'redux-saga';
import { createStore, applyMiddleware, compose } from 'redux';
import { reducer } from '../screens/App/shared/reducers/App';
import rootSaga from 'saga/index';
import DevTools from '../screens/App/containers/DevTools';
import thunkMiddleware from 'redux-thunk';
import { loadState, saveState } from '../screens/App/shared/utils/LocalStorage';
import * as config from '../screens/App/shared/configuration';
import { throttle } from 'lodash';
import * as sharedActions from 'actions/App';
import * as helperFunctions from 'utils/HelperFunctions';
import * as constants from 'constants/App';
import {callLogService} from 'utils/CallAPI';
import { useNavigate, useLocation } from 'react-router-dom';

/* This class has following responsibilities:
    1) Inject redux provider into component tree
    2) Create a redux store with saga, thunk and Devtools middlewares 
    3) Provide the router instance received to the saga middlewares using the approach described below:
    https://github.com/ReactTraining/react-router/issues/3972
*/

const configureStore = (initialState, context = {}, isDevelopment) => {

    const sagaMiddleware = createSagaMiddleware();

    var createStoreWithMiddleware;

    if (isDevelopment) {
        createStoreWithMiddleware = compose(
            applyMiddleware(thunkMiddleware, sagaMiddleware),
            DevTools.instrument()
        )(createStore);
    }
    else {
        createStoreWithMiddleware = compose(
            applyMiddleware(thunkMiddleware, sagaMiddleware)
        )(createStore);
    }

    const store = createStoreWithMiddleware(reducer, loadState());

    sagaMiddleware.run(rootSaga, context);

    const saveStateThrottled = throttle(function () { saveState(store.getState()) },
        config.throttledSaveStateInSeconds * 1000);

    store.subscribe(() => {
        saveStateThrottled();
    });
   
    return store;
};

const ProviderWithRouter = (props) => {
    const navigate = useNavigate();
    const location = useLocation();

    const toNavigateOptions = (stateOrOptions, isReplace) => {
        if (stateOrOptions === undefined) {
            return isReplace ? { replace: true } : undefined;
        }

        if (
            stateOrOptions !== null
            && typeof stateOrOptions === 'object'
            && (Object.prototype.hasOwnProperty.call(stateOrOptions, 'state')
                || Object.prototype.hasOwnProperty.call(stateOrOptions, 'replace'))
        ) {
            return isReplace ? { ...stateOrOptions, replace: true } : stateOrOptions;
        }

        return isReplace ? { state: stateOrOptions, replace: true } : { state: stateOrOptions };
    };
    
    // Create a history-like object for sagas
    const history = {
        push: (path, stateOrOptions) => navigate(path, toNavigateOptions(stateOrOptions, false)),
        replace: (path, stateOrOptions) => navigate(path, toNavigateOptions(stateOrOptions, true)),
        goBack: () => navigate(-1),
        goForward: () => navigate(1),
        go: (n) => navigate(n),
        location: location
    };

    const storeRef = useRef(null);

    if (!storeRef.current) {
        storeRef.current = configureStore(undefined, {
            history: history,
        }, props.isDevelopment);
    }

    return (
        <Provider store={storeRef.current}>
            <div>
                {props.children}
                {props.isDevelopment ? <DevTools store={storeRef.current} /> : false}
            </div>
        </Provider>
    );
};

export default ProviderWithRouter;